import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  Code2,
  ExternalLink,
  Sparkles,
  Palette,
} from "lucide-react";
import { toast } from "sonner";

const SITE_URL = "https://digitalrevolutionradio.com";
const EMBED_PATH = "/embed";

/**
 * /widget — public showcase / documentation page for the embeddable
 * "Now Spinning" widget. Broadcasters, streamers, blogs, or fans can
 * copy the iframe snippet and paste it anywhere.
 *
 * Structure:
 *   1. Hero explanation
 *   2. Live preview of the widget in the currently-selected style
 *   3. Configurator (size / theme / accent / transparent)
 *   4. Copy-paste embed snippet (iframe + markdown-friendly HTML)
 *   5. Preset examples (Twitch overlay, blog sidebar, WordPress, Discord)
 */
export default function Widget() {
  const [size, setSize] = useState("standard"); // "standard" | "compact"
  const [theme, setTheme] = useState("dark"); // "dark" | "light"
  const [transparent, setTransparent] = useState(false);
  const [accent, setAccent] = useState("dc2626"); // hex without #
  const [copied, setCopied] = useState(false);
  const previewIframeRef = useRef(null);

  // Compose the embed URL based on the current configurator state. Only
  // add params that differ from the defaults so shared snippets stay clean.
  const embedSrc = useMemo(() => {
    const params = new URLSearchParams();
    if (theme !== "dark") params.set("theme", theme);
    if (size === "compact") params.set("compact", "1");
    if (transparent) params.set("transparent", "1");
    if (accent && accent.toLowerCase() !== "dc2626") params.set("accent", accent);
    const q = params.toString();
    return `${SITE_URL}${EMBED_PATH}${q ? `?${q}` : ""}`;
  }, [theme, size, transparent, accent]);

  const iframeHeight = size === "compact" ? 60 : 88;
  const embedSnippet = useMemo(
    () =>
      `<iframe src="${embedSrc}" width="100%" height="${iframeHeight}" style="border:0;max-width:520px" scrolling="no" title="Digital Revolution Radio — Now Spinning"></iframe>`,
    [embedSrc, iframeHeight],
  );

  // Reload the preview iframe whenever the URL changes so we're always
  // showing the current configuration.
  useEffect(() => {
    if (previewIframeRef.current) {
      previewIframeRef.current.src = embedSrc;
    }
  }, [embedSrc]);

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Embed code copied 🤘");
    } catch (e) {
      toast.error("Copy failed — long-press to select");
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-zinc-50"
      data-testid="widget-page"
    >
      {/* Slim brand strip — matches /contest header */}
      <div className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase text-zinc-400 hover:text-red-500 transition-colors font-display"
            data-testid="widget-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            back_to_the_signal
          </Link>
          <div className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-display flex items-center gap-2">
            <Code2 className="w-3 h-3" />
            //_WIDGET · v1
          </div>
        </div>
      </div>

      {/* Hero */}
      <section
        className="relative border-b border-zinc-800 overflow-hidden"
        data-testid="widget-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, transparent 0 14px, rgba(220,38,38,1) 14px 16px)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-3 font-display flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            //_PUT_DRR_ON_YOUR_SITE
          </div>
          <h1
            className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tighter text-zinc-50 glitch"
            data-testid="widget-hero-title"
          >
            THE <span className="text-red-600">&quot;NOW SPINNING&quot;</span>
            <br />
            WIDGET<span className="text-red-600">.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-zinc-300 leading-snug">
            Drop a live DRR &quot;Now Spinning&quot; strip onto any website, blog,
            Twitch overlay, WordPress sidebar, or fan page. Updates itself
            every 15 seconds. Bass-red pulse. One click and your visitor
            tunes in.
          </p>
          <p className="mt-3 text-sm text-zinc-400">
            No account. No script. Just an{" "}
            <span className="text-red-500 font-mono">&lt;iframe&gt;</span>.
          </p>
        </div>
      </section>

      {/* Live Preview + Configurator */}
      <section
        className="border-b border-zinc-800 bg-zinc-950"
        data-testid="widget-preview-section"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="text-xs tracking-[0.4em] text-zinc-400 uppercase mb-6 font-display">
            //_LIVE_PREVIEW
          </div>

          {/* Preview area — checker background hints "your site here" */}
          <div
            className="p-6 md:p-10 border border-zinc-800"
            style={{
              background:
                "repeating-conic-gradient(#0a0a0a 0deg 90deg, #131316 90deg 180deg) 0 0 / 22px 22px",
            }}
            data-testid="widget-preview-canvas"
          >
            <iframe
              ref={previewIframeRef}
              src={embedSrc}
              width="100%"
              height={iframeHeight}
              scrolling="no"
              title="Widget preview"
              style={{
                border: 0,
                maxWidth: 520,
                display: "block",
                margin: "0 auto",
              }}
              data-testid="widget-preview-iframe"
            />
          </div>

          {/* Configurator */}
          <div
            className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-800"
            data-testid="widget-configurator"
          >
            <ConfigTile
              label="Size"
              options={[
                { v: "standard", label: "Standard" },
                { v: "compact", label: "Compact" },
              ]}
              value={size}
              onChange={setSize}
              testId="widget-config-size"
            />
            <ConfigTile
              label="Theme"
              options={[
                { v: "dark", label: "Dark" },
                { v: "light", label: "Light" },
              ]}
              value={theme}
              onChange={setTheme}
              testId="widget-config-theme"
            />
            <ConfigTile
              label="Background"
              options={[
                { v: false, label: "Solid" },
                { v: true, label: "Transparent" },
              ]}
              value={transparent}
              onChange={setTransparent}
              testId="widget-config-transparent"
            />
            <div className="bg-black p-4 border-l border-zinc-800">
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3 flex items-center gap-1.5">
                <Palette className="w-3 h-3" />
                Accent
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={`#${accent}`}
                  onChange={(e) => setAccent(e.target.value.replace("#", ""))}
                  className="w-10 h-10 bg-black border border-zinc-800 cursor-pointer"
                  aria-label="Accent color"
                  data-testid="widget-config-accent"
                />
                <input
                  type="text"
                  value={accent}
                  onChange={(e) =>
                    setAccent(e.target.value.replace(/[^0-9a-f]/gi, "").slice(0, 6))
                  }
                  className="flex-1 bg-black border border-zinc-800 focus:border-red-600 focus:outline-none px-2 py-2 text-sm text-zinc-100 font-mono tracking-widest uppercase"
                  placeholder="dc2626"
                  data-testid="widget-config-accent-hex"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embed snippet */}
      <section
        className="border-b border-zinc-800 bg-black"
        data-testid="widget-snippet-section"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-2 font-display">
                //_COPY_PASTE
              </div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-zinc-50">
                THE <span className="text-red-600">EMBED CODE</span>.
              </h2>
            </div>
            <button
              type="button"
              onClick={copySnippet}
              className="inline-flex items-center gap-2 border-2 border-red-600 bg-red-600 text-white hover:bg-white hover:text-black px-5 py-3 text-xs tracking-widest uppercase font-display transition-colors"
              data-testid="widget-copy-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" /> COPIED!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> COPY EMBED
                </>
              )}
            </button>
          </div>

          <pre
            className="bg-zinc-950 border border-zinc-800 p-4 md:p-5 text-xs md:text-sm text-zinc-200 overflow-x-auto font-mono leading-relaxed"
            data-testid="widget-snippet-code"
          >
            <code>{embedSnippet}</code>
          </pre>

          <p className="mt-4 text-sm text-zinc-500 leading-relaxed">
            The widget auto-refreshes the track every 15 seconds. Users
            tapping <span className="text-red-500 font-mono">TUNE IN</span>{" "}
            open{" "}
            <a
              href={SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-100 underline underline-offset-2 hover:text-red-500 transition-colors"
            >
              digitalrevolutionradio.com
              <ExternalLink className="w-3 h-3 inline ml-1" />
            </a>{" "}
            in a new tab — your visitors stay on your site.
          </p>
        </div>
      </section>

      {/* Preset recipes */}
      <section
        className="border-b border-zinc-800 bg-zinc-950"
        data-testid="widget-recipes"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="text-xs tracking-[0.4em] text-zinc-400 uppercase mb-6 font-display">
            //_RECIPES
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-zinc-50 mb-8">
            WHERE TO DROP IT<span className="text-red-600">.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-800">
            {RECIPES.map((r, i) => (
              <div
                key={r.title}
                className={`p-5 md:p-6 bg-black flex flex-col gap-3 ${
                  i < RECIPES.length - 1
                    ? "border-b lg:border-b-0 lg:border-r border-zinc-800"
                    : ""
                } ${i < RECIPES.length - 2 ? "md:border-r md:last:border-r-0" : ""}`}
                data-testid={`widget-recipe-${r.slug}`}
              >
                <div className="font-display text-red-600 text-4xl leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display text-lg text-zinc-50 tracking-tight">
                  {r.title}
                </div>
                <p className="text-sm text-zinc-400 leading-snug">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-display">
          <span>© {new Date().getFullYear()} digital revolution radio</span>
          <Link
            to="/"
            className="hover:text-red-500 transition-colors"
            data-testid="widget-footer-home"
          >
            ← RETURN TO STATION
          </Link>
        </div>
      </footer>
    </div>
  );
}

function ConfigTile({ label, options, value, onChange, testId }) {
  return (
    <div className="bg-black p-4 border-l first:border-l-0 border-zinc-800">
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {options.map((o) => (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 border text-xs tracking-widest uppercase font-display transition-colors ${
              value === o.v
                ? "bg-red-600 border-red-600 text-white"
                : "bg-black border-zinc-800 text-zinc-400 hover:text-red-500 hover:border-red-600"
            }`}
            data-testid={`${testId}-${String(o.v)}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const RECIPES = [
  {
    slug: "blog",
    title: "BLOG / FAN SITE",
    body: "Paste into your WordPress sidebar or blog post. Standard size + solid background fits any theme.",
  },
  {
    slug: "twitch",
    title: "TWITCH OVERLAY",
    body: "Use transparent background + compact size in an OBS Browser Source at 520×60px. Perfect for reaction streams.",
  },
  {
    slug: "band-page",
    title: "BAND PAGE",
    body: "If DRR spins your band, show it live on your site. Match the accent color to your brand.",
  },
  {
    slug: "discord",
    title: "DISCORD / DEVELOPER",
    body: "Or hit the public JSON at /api/now-playing to build your own bot, widget, or overlay.",
  },
];
